import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateUser } from "@/hooks/useCreateUser";
import { useParentUserCreation } from "@/hooks/useParentUserCreation";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { DeviceIdDisplay } from "./DeviceIdDisplay";
import { Loader2 } from "lucide-react";
import { useQuery as useConvexQuery, useMutation as useConvexMutation, useAction } from "convex/react";
import { api } from "../../api";
import { useToast } from "@/hooks/use-toast";
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';

interface AddUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AddUserDialog = ({ isOpen, onClose }: AddUserDialogProps) => {
  const [userType, setUserType] = useState<string>("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState(""); // For parent users
  const [selectedFamilyGroup, setSelectedFamilyGroup] = useState<string>("");
  const [childFirstName, setChildFirstName] = useState("");
  const [childLastName, setChildLastName] = useState("");
  const [childEmail, setChildEmail] = useState("");
  const [childPhone, setChildPhone] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [deviceId, setDeviceId] = useState<string>("");
  const [showDeviceId, setShowDeviceId] = useState(false);
  const [invitorFirstName, setInvitorFirstName] = useState("");
  const [invitorLastName, setInvitorLastName] = useState("");
  const [invitorEmail, setInvitorEmail] = useState("");

  // Validation helpers
  const isPasswordValid = password.length >= 8;
  const isPhoneValid = !phone || isValidPhoneNumber(phone);
  const isChildPhoneValid = !childPhone || isValidPhoneNumber(childPhone);
  const { createUser } = useCreateUser();
  const { createParentUser, isLoading: isCreatingParent, currentStep } = useParentUserCreation();
  const currentUser = useCurrentUser();
  const { toast } = useToast();
  // Note: sendInvite function is not available in current API

  // Fetch all family groups
  const allGroupsQuery = useConvexQuery(
    api.groups.list.all,
    currentUser?.isAuthenticated ? {
      paginationOpts: {
        numItems: 100,
        cursor: null
      }
    } : "skip"
  );
  
  // Debug: Log the raw groups data to understand structure
  console.log("All groups data:", allGroupsQuery?.page);
  
  // Filter to only show family type groups
  // API now returns group_id for each family group
  const familyGroups = allGroupsQuery?.page
    ?.filter((group: any) => {
      console.log("Group structure:", group); // Debug individual groups
      return group.type === "family";
    })
    .map((group: any) => ({
      id: group.group_id, // Use the new group_id field from API
      name: group.name,
      owner: group.owner
    })) || [];
    
  console.log("Filtered family groups:", familyGroups);

  const handleSubmit = async () => {
    if (!userType) return;
    
    // Validate based on user type
    if (userType === "mentor") {
      if (!invitorFirstName || !invitorLastName || !invitorEmail || !email) return;
    } else {
      if (!firstName || !lastName || !email) return;
    }

    setIsLoading(true);
    try {
      if (userType === "parent") {
        // Use the new 5-step parent creation flow
        if (!childFirstName || !childLastName) {
          throw new Error("Child information is required for parent users");
        }

        const result = await createParentUser({
          firstName,
          lastName,
          email,
          phone,
          password,
          childFirstName,
          childLastName,
          childEmail,
          childPhone,
        });

        setDeviceId(result.deviceId);
        setShowDeviceId(true);
      } else if (userType === "self_managed") {
        // Validate and format phone number to E.164 format
        let formattedPhone = phone;
        
        if (phone) {
          try {
            const phoneNumber = parsePhoneNumber(phone, 'US');
            if (phoneNumber && phoneNumber.isValid()) {
              formattedPhone = phoneNumber.format('E.164');
            } else {
              toast({
                title: "Invalid Phone Number",
                description: "Please enter a valid phone number with country code (e.g., +12025551234)",
                variant: "destructive",
              });
              setIsLoading(false);
              return;
            }
          } catch (error) {
            console.error("Phone number formatting error:", error);
            toast({
              title: "Invalid Phone Number",
              description: "Please enter a valid phone number with country code (e.g., +12025551234)",
              variant: "destructive",
            });
            setIsLoading(false);
            return;
          }
        }

        console.log("Frontend sending self-managed user data:", {
          userType,
          firstName,
          lastName,
          email,
          phone: formattedPhone,
          passwordLength: password?.length
        });
        const result = await createUser({
          userType,
          firstName,
          lastName,
          email,
          phone: formattedPhone,
          password
        });

        if (result?.deviceId) {
          setDeviceId(result.deviceId);
          setShowDeviceId(true);
        } else {
          handleClose();
        }
      } else if (userType === "mentor") {
        // Note: Mentor invite functionality not available in current API
        toast({
          title: "Not Available",
          description: "Mentor invite functionality is currently not available",
          variant: "destructive",
        });
        
        handleClose();
      } else {
        // Use the existing flow for other user types
        const userData = {
          userType,
          firstName,
          lastName,
          email,
          phone
        };

        // Add family group if user is a child and selectedFamilyGroup is set
        if (userType === "child") {
          if (!selectedFamilyGroup) {
            throw new Error("Family group selection is required for child users");
          }
          Object.assign(userData, {
            familyGroupId: selectedFamilyGroup
          });
        }
        await createUser(userData);
        handleClose();
      }
    } catch (error) {
      console.error("Failed to create user:", error);
      toast({
        title: "Error",
        description: "Failed to process request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setUserType("");
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setPassword("");
    setSelectedFamilyGroup("");
    setChildFirstName("");
    setChildLastName("");
    setChildEmail("");
    setChildPhone("");
    setDeviceId("");
    setShowDeviceId(false);
    setInvitorFirstName("");
    setInvitorLastName("");
    setInvitorEmail("");
    onClose();
  };

  const isFormValid = userType && 
    ((userType !== "mentor") ? (firstName && lastName && email) : (invitorFirstName && invitorLastName && invitorEmail && email)) &&
    ((userType !== "parent" && userType !== "self_managed") || (password && isPasswordValid)) &&
    (userType !== "parent" || (childFirstName && childLastName)) &&
    (userType !== "child" || selectedFamilyGroup) && 
    (userType !== "self_managed" || (phone && isPhoneValid)) &&
    (userType === "mentor" || (isPhoneValid && isChildPhoneValid));

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{showDeviceId ? "Device Created Successfully" : "Add New User"}</DialogTitle>
        </DialogHeader>

        {showDeviceId ? (
          <DeviceIdDisplay deviceId={deviceId} onClose={handleClose} />
        ) : (
          <>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="userType">User Type</Label>
                <Select value={userType} onValueChange={setUserType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select user type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parent">Parent</SelectItem>
                    <SelectItem value="child">Child</SelectItem>
                    <SelectItem value="mentor">Mentor</SelectItem>
                    <SelectItem value="self_managed">Self Managed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {userType === "child" && (
                <div className="space-y-2">
                  <Label htmlFor="familyGroup">Family Group</Label>
                  <Select 
                    value={selectedFamilyGroup || undefined} 
                    onValueChange={(value) => {
                      console.log('Selected family group:', value); // Debug log
                      setSelectedFamilyGroup(value);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select family group..." />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border border-border shadow-lg z-50 max-h-60 overflow-y-auto">
                      {familyGroups.length > 0 ? (
                        familyGroups.map((group: any) => (
                          <SelectItem 
                            key={group.id} 
                            value={group.id}
                            className="cursor-pointer hover:bg-accent hover:text-accent-foreground"
                          >
                            {group.name}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="px-2 py-1.5 text-sm text-muted-foreground">
                          No family groups available
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {userType === "mentor" ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="invitorFirstName">Invitor First Name</Label>
                    <Input
                      id="invitorFirstName"
                      value={invitorFirstName}
                      onChange={(e) => setInvitorFirstName(e.target.value)}
                      placeholder="Enter invitor first name..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="invitorLastName">Invitor Last Name</Label>
                    <Input
                      id="invitorLastName"
                      value={invitorLastName}
                      onChange={(e) => setInvitorLastName(e.target.value)}
                      placeholder="Enter invitor last name..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="invitorEmail">Invitor Email</Label>
                    <Input
                      id="invitorEmail"
                      type="email"
                      value={invitorEmail}
                      onChange={(e) => setInvitorEmail(e.target.value)}
                      placeholder="Enter invitor email..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="mentorEmail">Mentor Email</Label>
                    <Input
                      id="mentorEmail"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter mentor email..."
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Enter first name..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Enter last name..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter email address..."
                    />
                  </div>
                </>
              )}

              {userType !== "mentor" && (
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone {userType === "self_managed" ? "" : "(Optional)"}</Label>
                  <Input
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Enter phone number with country code (e.g., +12025551234)..."
                  />
                  {phone && !isPhoneValid && (
                    <p className="text-sm text-destructive">
                      Please enter a valid phone number with country code (e.g., +12025551234)
                    </p>
                  )}
                </div>
              )}

              {(userType === "parent" || userType === "self_managed") && (
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password (minimum 8 characters)..."
                  />
                  {password && !isPasswordValid && (
                    <p className="text-sm text-destructive">
                      Password must be at least 8 characters long
                    </p>
                  )}
                </div>
              )}

              {userType === "parent" && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4 dark:bg-blue-950/30 dark:border-blue-800">
                  <h3 className="text-sm font-medium text-foreground mb-2">Child Information</h3>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="childFirstName" className="text-xs">Child's First Name</Label>
                      <Input
                        id="childFirstName"
                        value={childFirstName}
                        onChange={(e) => setChildFirstName(e.target.value)}
                        placeholder="First name..."
                        className="h-8 text-sm"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="childLastName" className="text-xs">Child's Last Name</Label>
                      <Input
                        id="childLastName"
                        value={childLastName}
                        onChange={(e) => setChildLastName(e.target.value)}
                        placeholder="Last name..."
                        className="h-8 text-sm"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="childEmail" className="text-xs">Email (Optional)</Label>
                      <Input
                        id="childEmail"
                        type="email"
                        value={childEmail}
                        onChange={(e) => setChildEmail(e.target.value)}
                        placeholder="Email..."
                        className="h-8 text-sm"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="childPhone" className="text-xs">Phone (Optional)</Label>
                      <Input
                        id="childPhone"
                        value={childPhone}
                        onChange={(e) => setChildPhone(e.target.value)}
                        placeholder="Phone with country code..."
                        className="h-8 text-sm"
                      />
                      {childPhone && !isChildPhoneValid && (
                        <p className="text-xs text-destructive">
                          Please enter a valid phone number with country code
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={!isFormValid || isLoading || isCreatingParent}
              >
                {(isLoading || isCreatingParent) ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {currentStep || "Creating..."}
                  </>
                ) : userType === "mentor" ? (
                  "Send Invite"
                ) : userType === "parent" || userType === "self_managed" ? (
                  "Create User & Device"
                ) : (
                  "Create User"
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};